import React, { useMemo } from 'react';
import { useAuth } from '../../../../app/providers/AuthProvider';
import { resolveMainDashboardReport } from '../../../reports/api/reports';
import { useReportsConfig } from '../../../reports/hooks/useReportsConfig';
import ReportViewer from '../../../reports/components/ReportViewer';
import { LoadingBlock, EmptyState } from '../../../../shared/ui/composites';

/**
 * Home dashboard (`/`).
 * Always a Report Maker report assigned to the viewer's job.
 * Operators design KPI cards, charts, and the target tab in `/reports/designer`.
 */
export default function DashboardPage() {
  const { profile, loading: authLoading } = useAuth();
  const { data, isLoading, isError, refetch, isFetching } = useReportsConfig({
    profile,
    enabled: !!profile && !authLoading,
  });

  const config = data?.parsed;
  const ready = !authLoading && (profile ? !isLoading : true);

  const homeReport = useMemo(() => {
    if (isError || !config) return null;
    return resolveMainDashboardReport(config);
  }, [config, isError]);

  if (!ready) {
    return (
      <div className="min-h-screen w-full bg-background p-6 rtl">
        <LoadingBlock />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen w-full bg-background p-[var(--content-padding)] rtl max-md:p-3">
        <EmptyState
          message="دریافت داشبورد با خطا مواجه شد."
          actionLabel={isFetching ? 'در حال تلاش…' : 'تلاش مجدد'}
          onAction={() => refetch()}
        />
      </div>
    );
  }

  if (homeReport) {
    const tabs = (homeReport.tabs || [])
      .filter((t) => t.visible !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-background p-[var(--content-padding)] rtl max-md:p-3">
        <div className="w-full min-w-0 overflow-hidden rounded-[18px] bg-card shadow-sm ring-1 ring-slate-200/70">
          <ReportViewer
            title={homeReport.name || 'داشبورد'}
            report={{ ...homeReport, tabs }}
            profile={profile}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-background p-[var(--content-padding)] rtl max-md:p-3">
      <div className="mb-4 rounded-[18px] border-b-2 border-indigo-200 bg-[#eef2ff] px-8 py-5 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-800">داشبورد</h1>
      </div>
      <EmptyState message="برای این نقش هنوز داشبوردی در طراح گزارش ساخته نشده است." />
    </div>
  );
}
